import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useHeaderScroll } from '../../../hooks/header/useHeaderScroll';
import { getPageType } from '../../../utils/header/getPageType';
import { GLASS_EFFECT_SCROLL_THRESHOLD, GLASS_EFFECT_PAGES } from '../../../config/header/constants';
import './Header.css';

const Header = () => {
    const location = useLocation();
    const { isVisible, scrollY } = useHeaderScroll();
    const [menuOpen, setMenuOpen] = useState(false);
    const [notifVisible, setNotifVisible] = useState(true);
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

    // Keep isMobile in sync with viewport resizes
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    // On mobile the full-width notif bar pushes the header down; on desktop the bar floats above
    const NOTIF_HEIGHT = isMobile ? 32 : 0;

    // Lock / unlock body scroll + block synthetic wheel/touch scroll when menu is open
    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';

        if (!menuOpen) return;

        const blockScroll = (e) => e.stopPropagation();

        // Capture phase fires before the HomePage's window listeners, blocking them
        window.addEventListener('wheel', blockScroll, { capture: true, passive: true });
        window.addEventListener('touchmove', blockScroll, { capture: true, passive: false });
        window.addEventListener('touchstart', blockScroll, { capture: true, passive: true });

        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('wheel', blockScroll, { capture: true });
            window.removeEventListener('touchmove', blockScroll, { capture: true });
            window.removeEventListener('touchstart', blockScroll, { capture: true });
        };
    }, [menuOpen]);

    // Memoize page type calculation
    const pageType = useMemo(() => getPageType(location.pathname), [location.pathname]);
    
    // Memoize glass effect calculation
    const shouldApplyGlassEffect = useMemo(() => {
        return GLASS_EFFECT_PAGES.includes(pageType) && scrollY > GLASS_EFFECT_SCROLL_THRESHOLD;
    }, [pageType, scrollY]);

    // Memoize header className
    const headerClassName = useMemo(() => {
        return shouldApplyGlassEffect 
            ? `main-header main-header--${pageType}`
            : 'main-header';
    }, [shouldApplyGlassEffect, pageType]);

    if (!isVisible) {
        return null;
    }

    return (
        <>
            {/* Notification bar */}
            <div className={`notif-bar${notifVisible ? '' : ' notif-bar--dismissed'}`}>
                <img src="/icons/Left Shape.svg" className="notif-shape notif-shape--left" alt="" aria-hidden="true" />
                <div className="notif-bar-pill">
                    <a
                        href="https://videopress.com/v/zKA5efTw"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="notif-bar-message"
                    >
                        <span className="notif-bar-text">Introducing Kai, the AI Support Engineer</span>
                    </a>
                    <button
                        className="notif-bar-close"
                        onClick={() => setNotifVisible(false)}
                        aria-label="Dismiss notification"
                    >
                        <img src="/icons/X.svg" alt="Close" className="notif-bar-close-icon" />
                    </button>
                </div>
                <img src="/icons/Right Shape.svg" className="notif-shape notif-shape--right" alt="" aria-hidden="true" />
            </div>

            <header
                className={headerClassName}
                style={{ top: notifVisible ? NOTIF_HEIGHT : 0, transition: 'top 0.35s ease' }}
            >
                <div className="header-container">
                    {/* Logo and Company Name */}
                    <Link to="/" className="header-left">
                        <div className="header-logo">
                            <img src="/logos/kahuna-svg-logo.svg" alt="Kahuna Labs" className="header-logo-img" />
                        </div>
                    </Link>

                    {/* Call to Action Button */}
                    <div className="header-right">
                        <a
                            href="https://tally.so/r/dW9aBD"
                            className={`header-cta-button${menuOpen ? ' header-cta-button--hidden' : ''}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span>View demo</span>
                        </a>
                        {/* Desktop hamburger — stays in header */}
                        <button
                            className={`hamburger-button hamburger-button--desktop${menuOpen ? ' hamburger-button--open' : ''}`}
                            onClick={() => setMenuOpen(prev => !prev)}
                            aria-label="Toggle menu"
                            aria-expanded={menuOpen}
                        >
                            <span className="hamburger-line" />
                            <span className="hamburger-line" />
                            <span className="hamburger-line" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile floating hamburger — outside header so it's truly viewport-fixed */}
            <button
                className={`hamburger-button hamburger-button--mobile${menuOpen ? ' hamburger-button--open' : ''}`}
                onClick={() => setMenuOpen(prev => !prev)}
                aria-label="Toggle menu"
                aria-expanded={menuOpen}
            >
                <span className="hamburger-line" />
                <span className="hamburger-line" />
                <span className="hamburger-line" />
            </button>

            {/* Backdrop blur overlay */}
            <div
                className={`menu-backdrop${menuOpen ? ' menu-backdrop--visible' : ''}`}
                onClick={() => setMenuOpen(false)}
                aria-hidden="true"
            />

            {/* Slide-in nav menu */}
            <nav className={`nav-menu${menuOpen ? ' nav-menu--open' : ''}`} aria-hidden={!menuOpen}>
                <ul className="nav-menu-list">
                    {[
                        { to: '/about-us',      label: 'About' },
                        { to: '/blog',          label: 'Blog' },
                        { to: '/capabilities',  label: 'Capabilities' },
                    ].map(({ to, label }) => (
                        <li key={to}>
                            <Link
                                to={to}
                                className={`nav-menu-item${location.pathname === to ? ' nav-menu-item--active' : ''}`}
                                onClick={() => setMenuOpen(false)}
                            >
                                {label}
                            </Link>
                        </li>
                    ))}
                    <li>
                        <a
                            href="https://tally.so/r/dW9aBD"
                            className="nav-menu-item"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setMenuOpen(false)}
                        >
                            View Demo
                        </a>
                    </li>
                </ul>
            </nav>
        </>
    );
}

export default Header;
